// src/hooks/useMenuData.js
import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosConfig';
import { getStoredRoles } from '../utils/authSession';
import { normalizeMenuData } from '../utils/menuDataNormalizer';

const USER_ROLE_HEADER = 'role';
const SCHOOL_ID_HEADER = 'schoolId';
const DEFAULT_SCHOOL_ID = '1';

const useMenuData = (schoolId) => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMenu = useCallback(async () => {
    const resolvedSchoolId = schoolId ? String(schoolId).trim() : DEFAULT_SCHOOL_ID;
    try {
      setLoading(true);
      const storedRoles = getStoredRoles();
      const userRole = storedRoles.length ? storedRoles.join(',') : 'SCHOOL_ADMIN';
      const response = await axiosInstance.get('/api/menu', {
        headers: {
          [USER_ROLE_HEADER]: userRole,
          [SCHOOL_ID_HEADER]: resolvedSchoolId,
        },
        params: { schoolId: resolvedSchoolId },
      });
      const normalizedMenu = normalizeMenuData(response.data, resolvedSchoolId);
      setMenuData(normalizedMenu);
      localStorage.setItem('menuData', JSON.stringify(normalizedMenu));
      setError(null);
    } catch (err) {
      console.error('Error fetching menu:', err);
      const cachedMenuData = localStorage.getItem('menuData');
      if (cachedMenuData) {
        try {
          setMenuData(normalizeMenuData(JSON.parse(cachedMenuData), resolvedSchoolId));
        } catch (parseError) {
          console.error('Error parsing cached menu data:', parseError);
        }
      }
      setError(err.response?.data?.message || err.message || 'Failed to load menu.');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  return { menuData, loading, error, fetchMenu };
};

export default useMenuData;
